/**
 * Profile Page - Refactored with sub-components
 * Manages user profile, partner linking, groups, and account settings
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LogOut, Trash2, Crown, Globe, Share2, CreditCard, Zap, Gift } from "lucide-react";
import { FoundingBadgeCard } from "@/components/FoundingBadge";
import ProfileSettings from "@/components/ProfileSettings";
import MarkJointTripsModal from "@/components/premium/MarkJointTripsModal";
import ReferralPanel from "@/components/ReferralPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Import refactored sub-components
import {
  ProfileHeader,
  AccountSettingsSection,
  PartnerSection,
  GroupsSection,
  StreakBadgesSection,
  StatsSection,
  EditProfileModal,
  ChangePasswordModal,
  DeleteAccountModal,
  LinkPartnerModal,
  UnlinkPartnerModal,
  CreateGroupModal,
  JoinGroupModal,
  TransferOwnershipModal,
} from "@/components/profile";

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showShareSelectionModal, setShowShareSelectionModal] = useState(false);  // legacy, unused
  const [showJointTripsModal, setShowJointTripsModal] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  
  // Form state
  const [inviteCode, setInviteCode] = useState("");
  const [groupInviteCode, setGroupInviteCode] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [removePartnerItems, setRemovePartnerItems] = useState(false);
  const [selectedGroupForTransfer, setSelectedGroupForTransfer] = useState(null);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState("");
  const [editName, setEditName] = useState("");
  // Profile picture is now handled via direct upload, not URL
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  
  // Loading state
  const [linking, setLinking] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [joiningGroup, setJoiningGroup] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Data fetching
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchStreak = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/streak`);
      setStreakData(res.data);
    } catch (error) {
      console.error("Failed to load streak");
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      // Backend returns a bare List[GroupResponse]; tolerate either shape just in case
      setGroups(Array.isArray(res.data) ? res.data : (res.data.groups || []));
    } catch (error) {
      console.error("Failed to load groups");
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchGroups();
    fetchStreak();
  }, [fetchStats, fetchGroups, fetchStreak]);

  // Handlers
  const copyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(user.invite_code);
      setCopied(true);
      toast.success("Invite code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleLinkPartner = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    setLinking(true);
    try {
      const response = await axios.post(`${API}/partner/link`, { invite_code: inviteCode.trim() });
      await refreshUser();
      toast.success("Partner linked successfully!");
      setShowLinkModal(false);
      setInviteCode("");
      setNewPartnerName(response.data?.partner_name || "your partner");
      // Open the shared-trips picker — founder-sanctioned explicit confirmation
      setShowJointTripsModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link partner");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPartner = async () => {
    setUnlinking(true);
    try {
      await axios.post(`${API}/partner/unlink`, { remove_partner_items: removePartnerItems });
      await refreshUser();
      toast.success(removePartnerItems ? "Partner unlinked and their items removed" : "Partner unlinked");
      setShowUnlinkModal(false);
      setRemovePartnerItems(false);
    } catch (error) {
      toast.error("Failed to unlink partner");
    } finally {
      setUnlinking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    setCreatingGroup(true);
    try {
      const res = await axios.post(`${API}/groups`, { name: newGroupName.trim() });
      const inviteCode = res.data.invite_code;
      await fetchGroups();
      toast.success(
        <div>
          <p className="font-semibold">Group created!</p>
          <p className="text-sm mt-1">Invite code: <span className="font-mono font-bold">{inviteCode}</span></p>
          <p className="text-xs mt-1 text-muted-foreground">Share this code with friends to join!</p>
        </div>,
        { duration: 10000 }
      );
      setShowGroupModal(false);
      setNewGroupName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    if (!groupInviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    setJoiningGroup(true);
    try {
      const res = await axios.post(`${API}/groups/join`, { invite_code: groupInviteCode.trim() });
      await fetchGroups();
      toast.success(`Joined "${res.data.group_name}"!`);
      setShowJoinGroupModal(false);
      setGroupInviteCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group");
    } finally {
      setJoiningGroup(false);
    }
  };

  const handleLeaveGroup = async (groupId, groupName) => {
    if (!confirm(`Are you sure you want to leave "${groupName}"?`)) return;
    
    try {
      await axios.post(`${API}/groups/${groupId}/leave`);
      await fetchGroups();
      toast.success(`Left "${groupName}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave group");
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedGroupForTransfer || !selectedNewAdmin) return;
    
    setTransferring(true);
    try {
      await axios.post(`${API}/groups/${selectedGroupForTransfer.id}/transfer`, {
        new_admin_id: selectedNewAdmin
      });
      await fetchGroups();
      toast.success("Ownership transferred successfully!");
      setShowTransferModal(false);
      setSelectedGroupForTransfer(null);
      setSelectedNewAdmin("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer ownership");
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    if (!deletePassword) {
      toast.error("Please enter your password");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        data: { password: deletePassword, confirm_text: deleteConfirmText }
      });
      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEditProfile = () => {
    setEditName(user.name || "");
    setShowEditProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (editName.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }

    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, {
        name: editName.trim()
      });
      await refreshUser();
      toast.success("Profile updated successfully!");
      setShowEditProfileModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (currentPassword === newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background pb-24 overflow-y-auto">
      {/* Header */}
      <ProfileHeader user={user} onEditProfile={handleOpenEditProfile} onAvatarUpdated={refreshUser} />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Account Settings */}
        <AccountSettingsSection
          userName={user.name}
          onEditProfile={handleOpenEditProfile}
          onChangePassword={() => setShowPasswordModal(true)}
        />

        {/* Partner Section — moved up next to account settings */}
        <PartnerSection
          user={user}
          copied={copied}
          onCopyInviteCode={copyInviteCode}
          onLinkPartner={() => setShowLinkModal(true)}
          onUnlinkPartner={() => setShowUnlinkModal(true)}
        />

        {/* Groups Section — moved up next to account settings */}
        <GroupsSection
          user={user}
          groups={groups}
          onCreateGroup={() => setShowGroupModal(true)}
          onJoinGroup={() => setShowJoinGroupModal(true)}
          onLeaveGroup={handleLeaveGroup}
          onOpenGroup={(group) => navigate(`/dashboard?group=${group.id}`)}
          onTransferOwnership={(group) => {
            setSelectedGroupForTransfer(group);
            setShowTransferModal(true);
          }}
        />

        {/* Subscription & Premium Section - Highly Visible */}
        <section className="bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 border border-primary/20 rounded-2xl shadow-card overflow-hidden animate-fade-in">
          <div className="p-6 border-b border-primary/20">
            <h2 className="font-serif text-xl font-medium text-foreground flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Subscription & Premium
            </h2>
          </div>
          <div className="p-6 space-y-3">
            {/* Current Plan Status */}
            <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user.founding_explorer 
                    ? 'bg-gradient-to-br from-amber-400 to-amber-600' 
                    : user.subscription_type === 'duo' || user.subscription_type === 'explorer_pro'
                      ? 'bg-gradient-to-br from-primary to-secondary'
                      : 'bg-muted'
                }`}>
                  {user.founding_explorer ? (
                    <Crown className="w-5 h-5 text-white" />
                  ) : user.subscription_type === 'duo' || user.subscription_type === 'explorer_pro' ? (
                    <Zap className="w-5 h-5 text-white" />
                  ) : (
                    <Zap className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {user.founding_explorer 
                      ? 'Founding Explorer' 
                      : user.subscription_type === 'duo' 
                        ? 'Duo Plan' 
                        : user.subscription_type === 'explorer_pro'
                          ? 'Explorer Pro'
                          : 'Free Plan'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user.founding_explorer 
                      ? 'Lifetime Premium Access' 
                      : user.subscription_type === 'duo' || user.subscription_type === 'explorer_pro'
                        ? 'Premium features unlocked'
                        : 'Limited features'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Upgrade/Manage Button */}
            <Link to="/pricing" className="block">
              <Button 
                className={`w-full h-12 rounded-xl font-semibold ${
                  user.founding_explorer || user.subscription_type === 'duo' || user.subscription_type === 'explorer_pro'
                    ? 'bg-muted text-foreground hover:bg-muted/80'
                    : 'bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90'
                }`}
                data-testid="manage-subscription-btn"
              >
                {user.founding_explorer 
                  ? 'View Premium Features' 
                  : user.subscription_type === 'duo' || user.subscription_type === 'explorer_pro'
                    ? 'Manage Premium on duolyst.co.uk'
                    : 'Upgrade on duolyst.co.uk'}
              </Button>
            </Link>
            
            {/* Paywall Demo Link for Testing (only visible on localhost or preview) */}
            {(window.location.hostname.includes('localhost') || window.location.hostname.includes('preview')) && (
              <Link to="/paywall-demo" className="block">
                <Button variant="outline" className="w-full h-10 rounded-xl text-sm" data-testid="paywall-demo-btn">
                  Preview Premium Paywalls
                </Button>
              </Link>
            )}
          </div>
        </section>

        {/* Referral Rewards Section */}
        <section className="bg-gradient-to-r from-pink-500/5 via-purple-500/5 to-pink-500/5 border border-pink-500/20 rounded-2xl shadow-card overflow-hidden animate-fade-in">
          <div className="p-6 border-b border-pink-500/20">
            <h2 className="font-serif text-xl font-medium text-foreground flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-500" />
              Invite & Earn Rewards
            </h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-muted-foreground mb-4">
              Invite friends to DuoLyst and earn free premium days, exclusive badges, and more!
            </p>
            <Button 
              onClick={() => setShowReferrals(true)}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90"
              data-testid="referrals-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              View Referral Rewards
            </Button>
          </div>
        </section>

        {/* Founding Explorer Badge */}
        {user.founding_explorer ? (
          <section className="animate-fade-in">
            <FoundingBadgeCard 
              founderNumber={user.founding_explorer_number}
              joinedAt={user.founding_explorer_joined_at}
            />
          </section>
        ) : (
          <Link to="/founding-explorer" className="block animate-fade-in">
            <section className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-400/30 rounded-2xl p-4 hover:shadow-lg hover:shadow-amber-500/10 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-700 dark:text-amber-400 text-sm">
                    Join the Founding 200
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Get exclusive lifetime benefits
                  </p>
                </div>
              </div>
            </section>
          </Link>
        )}

        {/* Public Profile Toggle */}
        <ProfileSettings />

        {/* Streak Badges */}
        <StreakBadgesSection streakData={streakData} />

        {/* Stats */}
        <StatsSection stats={stats} />

        {/* Danger Zone */}
        <section className="bg-card rounded-2xl border border-red-200 shadow-card overflow-hidden animate-fade-in" style={{ animationDelay: "0.15s" }}>
          <div className="p-6 border-b border-red-200 bg-red-50/50">
            <h2 className="font-serif text-xl font-medium text-red-700">Danger Zone</h2>
          </div>
          <div className="p-6 space-y-3">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full h-12 justify-start rounded-xl border-charcoal/10 hover:bg-charcoal/5"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              <span className="flex-1 text-left">Log Out</span>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(true)}
              className="w-full h-12 justify-start rounded-xl border-red-200 text-red-600 hover:bg-red-50"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-3" />
              <span className="flex-1 text-left">Delete Account</span>
            </Button>
          </div>
        </section>
      </main>

      {/* Modals */}
      <EditProfileModal
        open={showEditProfileModal}
        onOpenChange={setShowEditProfileModal}
        editName={editName}
        setEditName={setEditName}
        currentAvatarUrl={user.preferences?.avatar_url}
        profilePicturePath={user.profile_picture_path}
        savingProfile={savingProfile}
        onSubmit={handleSaveProfile}
        onAvatarUploaded={refreshUser}
      />

      <ChangePasswordModal
        open={showPasswordModal}
        onOpenChange={setShowPasswordModal}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        showCurrentPassword={showCurrentPassword}
        setShowCurrentPassword={setShowCurrentPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        changingPassword={changingPassword}
        onSubmit={handleChangePassword}
      />

      <DeleteAccountModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        deletePassword={deletePassword}
        setDeletePassword={setDeletePassword}
        deleteConfirmText={deleteConfirmText}
        setDeleteConfirmText={setDeleteConfirmText}
        deleting={deleting}
        onSubmit={handleDeleteAccount}
      />

      <LinkPartnerModal
        open={showLinkModal}
        onOpenChange={setShowLinkModal}
        inviteCode={inviteCode}
        setInviteCode={setInviteCode}
        linking={linking}
        onSubmit={handleLinkPartner}
      />

      <UnlinkPartnerModal
        open={showUnlinkModal}
        onOpenChange={setShowUnlinkModal}
        partnerName={user?.partner_name}
        removePartnerItems={removePartnerItems}
        setRemovePartnerItems={setRemovePartnerItems}
        unlinking={unlinking}
        onConfirm={handleUnlinkPartner}
      />

      <CreateGroupModal
        open={showGroupModal}
        onOpenChange={setShowGroupModal}
        groupName={newGroupName}
        setGroupName={setNewGroupName}
        creating={creatingGroup}
        onSubmit={handleCreateGroup}
      />

      <JoinGroupModal
        open={showJoinGroupModal}
        onOpenChange={setShowJoinGroupModal}
        inviteCode={groupInviteCode}
        setInviteCode={setGroupInviteCode}
        joining={joiningGroup}
        onSubmit={handleJoinGroup}
      />

      <TransferOwnershipModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        group={selectedGroupForTransfer}
        userId={user.id}
        selectedNewAdmin={selectedNewAdmin}
        setSelectedNewAdmin={setSelectedNewAdmin}
        transferring={transferring}
        onConfirm={handleTransferOwnership}
      />

      <MarkJointTripsModal
        open={showJointTripsModal}
        onClose={() => setShowJointTripsModal(false)}
        onSaved={() => { /* dashboard will refetch on next open */ }}
      />

      {/* Referral Panel Modal */}
      <Dialog open={showReferrals} onOpenChange={setShowReferrals}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="referral-panel-description">
          <DialogHeader className="sr-only">
            <DialogTitle>Invite Friends & Earn Rewards</DialogTitle>
          </DialogHeader>
          <span id="referral-panel-description" className="sr-only">
            Share your referral code and earn free premium features
          </span>
          <ReferralPanel onClose={() => setShowReferrals(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
